/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Card, CardContent, Divider, FormControl, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, LinearProgress, OutlinedInput, Paper, Tooltip, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser, updatePasswordClient } from '../../actions/userAction';
import { Visibility, VisibilityOff, LockReset, CheckCircle, Cancel, AutoFixHigh, ContentCopy } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { t } from 'i18next';
import { resetError, resetUpdated } from '../../slices/userSlice';
import { showErrorToast, showSuccessToast } from '../../slices/toastSlice';
import Progress from '../../components/Loader/Progress ';
import { persistor } from '../../store/store';

function ChangePasswordClient() {
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [copied, setCopied] = useState(false);
    const [copyFailed, setCopyFailed] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { error: hasError, passwordUpdated, loading } = useSelector((state) => state.user) || {};


    const validationSchema = yup.object({
        oldPassword: yup.string().required(t('Old password is required')),
        newPassword: yup
            .string()
            .required(t('Password is required'))
            .matches(/^(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/, t('Password must be at least 8 characters long and contain at least one special character')),
        confirmPassword: yup
            .string()
            .required(t('Please confirm the password'))
            .oneOf([yup.ref('newPassword')], t('Passwords must match')),
    });

    const formik = useFormik({
        initialValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
        validationSchema,
        validateOnMount: true,
        onSubmit: (values) => {
            dispatch(updatePasswordClient({ oldPassword: values.oldPassword, newPassword: values.newPassword }));
        },
    });

    const generatePassword = (length = 14) => {
        const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const lower = 'abcdefghijklmnopqrstuvwxyz';
        const digits = '0123456789';
        const special = '!@#$%^&*';
        const all = upper + lower + digits + special;
        const pick = (chars) => chars[Math.floor(Math.random() * chars.length)];
        let pwd = pick(upper) + pick(lower) + pick(digits) + pick(special);
        for (let i = pwd.length; i < length; i++) pwd += pick(all);
        const arr = pwd.split('');
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr.join('');
    };

    const handleGenerate = () => {
        const pw = generatePassword(14);
        formik.setValues({ ...formik.values, newPassword: pw, confirmPassword: pw }, false);
        formik.validateForm();
        formik.setTouched({ ...formik.touched, newPassword: true, confirmPassword: true }, false);
        setCopied(false);
    };

    const handleCopy = async () => {
        const text = formik.values.newPassword || '';
        if (!text) return;
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
                return;
            }
            throw new Error('Clipboard API not available');
        } catch (_) {
            const ok = fallbackCopyToClipboard(text);
            if (ok) {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            } else {
                setCopyFailed(true);
                setTimeout(() => setCopyFailed(false), 1500);
            }
        }
    };

    function fallbackCopyToClipboard(text) {
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.top = '-1000px';
            textArea.style.left = '-1000px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            return successful;
        } catch (e) {
            return false;
        }
    }

    const pwdScore = useMemo(() => {
        const v = formik.values.newPassword || '';
        let score = 0;
        if (v.length >= 8) score += 34;
        if (/^[A-Za-z0-9!@#$%^&*]*$/.test(v)) score += 33;
        if (/[!@#$%^&*]/.test(v)) score += 33;
        return Math.min(100, score);
    }, [formik.values.newPassword]);

    useEffect(() => {
        if (passwordUpdated) {
            dispatch(showSuccessToast(passwordUpdated));
              dispatch(logoutUser())
                   persistor.purge();
                   navigate('/login')
        }
    }, [passwordUpdated, navigate, dispatch]);

    useEffect(() => {
        if (hasError) {
            dispatch(showErrorToast(hasError));
        }
    }, [hasError, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(resetUpdated());
            dispatch(resetError());
        };
    }, [dispatch]);

    const isSubmitting = loading === true || loading === 'pending';

    return (
        <Paper variant="p1" sx={{ p: { xs: 1, md: 3 } }}>
            {isSubmitting ? (
                <Progress Msg='Updating Password...' />
            ) : (
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px rgba(11,60,93,0.08)', border: '1px solid #e9ecef' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <LockReset sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('Change Password')}</Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        <Box component="form" onSubmit={formik.handleSubmit} noValidate>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel htmlFor="oldPassword">{t('Old Password')}</InputLabel>
                                        <OutlinedInput
                                            id="oldPassword"
                                            type={showOld ? 'text' : 'password'}
                                            name="oldPassword"
                                            value={formik.values.oldPassword}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.oldPassword && Boolean(formik.errors.oldPassword)}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton aria-label="toggle password visibility" onClick={() => setShowOld((s) => !s)} edge="end">
                                                        {showOld ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            label={t('Old Password')}
                                        />
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {formik.touched.oldPassword && formik.errors.oldPassword}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel htmlFor="newPassword">{t('New Password')}</InputLabel>
                                        <OutlinedInput
                                            id="newPassword"
                                            type={showNew ? 'text' : 'password'}
                                            name="newPassword"
                                            value={formik.values.newPassword}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <Tooltip title={t('Generate')}>
                                                        <IconButton aria-label="generate strong password" onClick={handleGenerate} edge="end">
                                                            <AutoFixHigh />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={copyFailed ? t('Copy failed') : (copied ? t('Copied') : t('Copy'))}>
                                                        <span>
                                                            <IconButton aria-label="copy password" onClick={handleCopy} edge="end" disabled={!formik.values.newPassword}>
                                                                <ContentCopy />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <IconButton aria-label="toggle password visibility" onClick={() => setShowNew((s) => !s)} edge="end">
                                                        {showNew ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            label={t('New Password')}
                                        />
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {formik.touched.newPassword && formik.errors.newPassword}
                                        </FormHelperText>
                                    </FormControl>
                                    <Box sx={{ mt: 1.5 }}>
                                        <LinearProgress variant="determinate" value={pwdScore} sx={{ height: 8, borderRadius: 1 }} />
                                        <Typography variant="caption" color="text.secondary">
                                            {t('Strength')}: {pwdScore <= 40 ? t('Weak') : pwdScore <= 80 ? t('Medium') : t('Strong')}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel htmlFor="confirmPassword">{t('Confirm Password')}</InputLabel>
                                        <OutlinedInput
                                            id="confirmPassword"
                                            type={showConfirm ? 'text' : 'password'}
                                            name="confirmPassword"
                                            value={formik.values.confirmPassword}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton aria-label="toggle password visibility" onClick={() => setShowConfirm((s) => !s)} edge="end">
                                                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            label={t('Confirm Password')}
                                        />
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {formik.touched.confirmPassword && formik.errors.confirmPassword}
                                        </FormHelperText>
                                    </FormControl>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1.5 }}>
                                        <Req ok={formik.values.newPassword.length >= 8} label={t('At least 8 characters')} />
                                        <Req ok={/^[A-Za-z0-9!@#$%^&*]*$/.test(formik.values.newPassword)} label={t('Only letters, numbers, and !@#$%^&*')} />
                                        <Req ok={/[!@#$%^&*]/.test(formik.values.newPassword)} label={t('At least one of !@#$%^&*')} />
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box sx={{ position: 'sticky', bottom: 0, mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1, bgcolor: 'background.paper', py: 1, borderTop: '1px solid', borderColor: 'divider', zIndex: 1 }}>
                                <Button variant="outlined" onClick={() => navigate('/')}>{t('Cancel')}</Button>
                                <Button variant="contained" type="submit" disabled={!formik.isValid || isSubmitting}>
                                    {isSubmitting ? t('Updating...') : t('Update Password')}
                                </Button>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Paper>
    )
}

export default ChangePasswordClient

function Req({ ok, label }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {ok ? <CheckCircle fontSize="small" color="success" /> : <Cancel fontSize="small" sx={{ color: 'text.disabled' }} />}
            <Typography variant="caption" color={ok ? 'success.main' : 'text.secondary'}>{label}</Typography>
        </Box>
    );
}